import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowLeft, Check, Wand2, MessageSquare, BookUser, PenLine, FileUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScreenshotUploader } from "@/components/ScreenshotUploader";
import { CustomerForm } from "@/components/CustomerForm";
import { DuplicateWarning } from "@/components/DuplicateWarning";
import { Button } from "@/components/ui/button";

const METHODS = [
  { key: "conversation", label: "From Conversation", icon: MessageSquare, desc: "AI reads chat screenshots" },
  { key: "passport", label: "From Passport", icon: BookUser, desc: "AI reads a passport once" },
  { key: "manual", label: "Manual Entry", icon: PenLine, desc: "Type what you know" },
];

export default function NewLead() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [method, setMethod] = useState(null);
  const [step, setStep] = useState("choose"); // choose | upload | processing | review
  const [files, setFiles] = useState([]);
  const [passportFile, setPassportFile] = useState(null);
  const [form, setForm] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [saving, setSaving] = useState(false);

  const emptyForm = () => ({
    name: "", company: "", phone: "", whatsapp: "", country: "",
    lead_source: "", product_interest: "", summary: "", status: "warm",
    next_followup_date: "", instagram: "", facebook: "", linkedin: "", website: "",
    date_of_birth: "", nationality: "", passport_number: "", passport_issue_date: "",
    passport_expiry_date: "", designation: "", email: "", city: "", notes: "",
    contact_type: "Customer", state: "", sub_agent: "", pi_number: "", position: "", department: "",
    screenshots: [], documents: [], consultant_id: user?.role === "admin" ? "" : user?.id,
  });

  const loadConsultants = async () => {
    if (user?.role === "admin") {
      const c = await api.get("/users");
      setConsultants(c.data);
    }
  };

  const choose = (key) => {
    setMethod(key);
    if (key === "manual") {
      setForm(emptyForm());
      loadConsultants();
      setStep("review");
    } else {
      setStep("upload");
    }
  };

  const processConversation = async () => {
    if (files.length === 0) { toast.error("Upload at least one screenshot first"); return; }
    setStep("processing");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("mode", "new");
    try {
      const r = await api.post("/ai/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setForm({
        ...emptyForm(),
        name: d.name || "", company: d.company || "", phone: d.phone || "", whatsapp: d.phone || "",
        country: d.country || "", lead_source: d.source || "", product_interest: d.product_interest || "",
        summary: d.summary || "", status: d.suggested_status || "warm",
        next_followup_date: d.suggested_followup_date || "", screenshots: d.screenshots || [],
      });
      await loadConsultants();
      setStep("review");
      toast.success("AI extracted the details — review and save");
    } catch (e) {
      toast.error(apiError(e, "AI processing failed"));
      setStep("upload");
    }
  };

  const processPassport = async () => {
    if (!passportFile) { toast.error("Upload a passport image or PDF first"); return; }
    setStep("processing");
    const fd = new FormData();
    fd.append("file", passportFile);
    try {
      const r = await api.post("/passport/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setForm({
        ...emptyForm(),
        name: d.name || "", country: d.country || "", city: d.city || "",
        date_of_birth: d.date_of_birth || "", nationality: d.nationality || "",
        passport_number: d.passport_number || "", passport_issue_date: d.passport_issue_date || "",
        passport_expiry_date: d.passport_expiry_date || "",
        lead_source: "Passport", documents: d.document ? [d.document] : [],
      });
      await loadConsultants();
      setStep("review");
      toast.success("Passport read — review and save");
    } catch (e) {
      toast.error(apiError(e, "Could not read passport"));
      setStep("upload");
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/customers", form);
      toast.success("Customer saved");
      navigate(`/customers/${r.data.id}`);
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto animate-in-up">
      <button
        data-testid="new-lead-back"
        onClick={() => (step === "choose" ? navigate(-1) : (setStep("choose"), setForm(null), setFiles([]), setPassportFile(null)))}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">New Customer</h1>
        <p className="text-muted-foreground mt-1">Add a lead in seconds — AI does the typing for you.</p>
      </div>

      {step === "choose" && (
        <div className="grid sm:grid-cols-3 gap-4">
          {METHODS.map((m) => (
            <button
              key={m.key}
              data-testid={`method-${m.key}`}
              onClick={() => choose(m.key)}
              className="text-left bg-white border border-border rounded-lg p-5 hover:border-primary/50 hover:-translate-y-1 transition-transform hover:shadow-md"
            >
              <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center mb-3">
                <m.icon className="h-5 w-5" />
              </div>
              <div className="font-display font-semibold tracking-tight">{m.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{m.desc}</div>
            </button>
          ))}
        </div>
      )}

      {step === "upload" && method === "conversation" && (
        <div className="space-y-6">
          <ScreenshotUploader files={files} setFiles={setFiles} />
          <Button data-testid="process-ai-button" onClick={processConversation} className="gap-2 font-semibold h-11 w-full">
            <Wand2 className="h-4 w-4" /> Process with AI
          </Button>
        </div>
      )}

      {step === "upload" && method === "passport" && (
        <div className="space-y-6">
          <label
            data-testid="passport-dropzone"
            className="cursor-pointer flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-secondary/40 transition-colors px-6 py-10 text-center"
          >
            <FileUp className="h-8 w-8 text-muted-foreground mb-3" strokeWidth={1.5} />
            <p className="text-sm font-medium">{passportFile ? passportFile.name : "Upload passport (image or PDF)"}</p>
            <p className="text-xs text-muted-foreground mt-1">JPG · PNG · PDF · processed by AI only once</p>
            <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => setPassportFile(e.target.files?.[0] || null)} />
          </label>
          {passportFile && (
            <button onClick={() => setPassportFile(null)} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-destructive">
              <X className="h-3 w-3" /> Remove file
            </button>
          )}
          <Button data-testid="process-passport-button" onClick={processPassport} className="gap-2 font-semibold h-11 w-full">
            <Wand2 className="h-4 w-4" /> Read Passport with AI
          </Button>
        </div>
      )}

      {step === "processing" && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="h-16 w-16 rounded-2xl bg-primary flex items-center justify-center mb-6">
            <Sparkles className="h-8 w-8 text-white animate-pulse" />
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            {method === "passport" ? "Reading the passport…" : "Reading the conversation…"}
          </h3>
          <p className="text-muted-foreground mt-2 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Extracting details
          </p>
        </div>
      )}

      {step === "review" && form && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {method !== "manual" && (
            <div className="flex items-center gap-2 text-sm bg-secondary rounded-md px-3 py-2">
              <Sparkles className="h-4 w-4" />
              <span className="font-medium">Review the AI draft.</span>
              <span className="text-muted-foreground">Edit anything, then save. No fields are required except a name.</span>
            </div>
          )}
          {form.documents?.length > 0 && (
            <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 rounded-md px-3 py-2">
              <BookUser className="h-4 w-4" /> Passport attached: {form.documents[0].filename}
            </div>
          )}
          <DuplicateWarning form={form} />
          <CustomerForm form={form} setForm={setForm} consultants={consultants} isAdmin={user?.role === "admin"} showConsultant />
          <div className="flex gap-3 sticky bottom-0 bg-background border-t border-border pt-4 pb-2 -mx-8 px-8">
            <Button data-testid="save-lead-button" onClick={save} disabled={saving} className="gap-2 font-semibold h-11 flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Contact
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
